import {Injectable}                                              from '@angular/core';
import {Validators}                                              from '@angular/forms';
import {delay, filter, map, startWith}                           from 'rxjs/operators';
import {combineLatest, Observable, pairwise}                     from 'rxjs';
import {
  ApDynformsConfigFieldset
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigComboBox
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {DriverStore}                                             from '../../../stores/docu/driver.store';
import {MachineStore}                                            from '../../../stores/docu/machine.store';
import {InstrumentStore}                                         from '../../../stores/docu/instrument.store';
import {ApDynformsValidator}                                     from '../../../ap-dynforms/ap-dynforms-validator';
import {FertilizerStore}                                         from '../../../stores/base-data/fertilizer.store';
import {
  FertilizerInorganicStore
}                                                                from '../../../stores/base-data/fertilizer.inorganic.store';
import {
  FertilizerOrganicStore
}                                                                from '../../../stores/base-data/fertilizer.organic.store';
import {CombinedFertilizer}                                      from '../../../types/common-types';
import {
  ApDynformsConfigNumerictextbox
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {
  ApTranslationService
}                                                                from '../../../ap-utils/service/ap-translation.service';
import {
  ApDynformsConfigPlaceholder
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {ApTimeSpanUtil}                                          from '../../../ap-utils';
import {
  ApDynformsConfigTextbox
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-textbox';
import {
  ApDynformsConfigTextarea
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-textarea';
import {ApplicationModesEnum}                                    from '../../../stores/common/appl-modes.store';
import {nameOf}                                                  from '../../../ap-core/utils/ap-name-of';
import {IOperationModeAppSetting}                                from '../../../types/application-settings.types';
import {
  ApDynformsConfigDatepicker
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-datepicker';
import {
  GetRoundNumericService
}                                                                from '../../../ap-utils/service/get-round-numeric.service';
import {EcStageMinMax, INFertilizationPlanFieldToSelectGridItem} from '../types/n-fertilization-planning-entry.types';
import {SettingsStore}                                           from '../../../stores/base-data/settings.store';
import {CampaignYearStore}                                       from '../../../stores/login/campaignyear.store';
import {ApDateService}                                           from '../../../ap-core/services/ap-date-service';
import {
  ApDynformsConfigBase
}                                                                from '../../../ap-dynforms/config/ap-dynforms-config-base';
import {
  NFertilizationPlanningEntryService
}                                                                from '../services/n-fertilization-planning-entry.service';
import {ApDynformsComponent}                                     from '../../../ap-dynforms/ap-dynforms.component';
import IDriver = Data.DocuContext.Driver.IDriver;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import { ApDateMinMaxValidator } from 'src/app/ap-dynforms/validators/ap-date-min-max-validator';
import { LanguageStore } from 'src/app/stores/translation/language.store';

@Injectable()
export class GeneralConfig {
  constructor(private driverStore: DriverStore,
              private machineStore: MachineStore,
              private settingsStore: SettingsStore,
              private fertilizerStore: FertilizerStore,
              private instrumentStore: InstrumentStore,
              private campaignYearStore: CampaignYearStore,
              private fertilizerOrganicStore: FertilizerOrganicStore,
              private fertilizerInorganicStore: FertilizerInorganicStore,
              private dateService: ApDateService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private nFertilizationPlanningEntryService: NFertilizationPlanningEntryService,
              private languageStore: LanguageStore) {
  }

  public getInfoFieldSets(dynForm: ApDynformsComponent, formValues: any, operationModeAppSetting: IOperationModeAppSetting,
                          operationModeAppSettings$: Observable<IOperationModeAppSetting[]>, growthStagesMinAndMax: EcStageMinMax,
                          selectedFields: INFertilizationPlanFieldToSelectGridItem[]): ApDynformsConfigFieldset[] {
    const crops = selectedFields.map((f) => f.Crop)
      .filter((v, i, a) => !!v && a.indexOf(v) === i);
    const totalArea = selectedFields.filter((f) => !!f.Area)
      .reduce((sum, o) => sum + o.Area, 0);
    const digitsAfterDecimal = this.settingsStore.FirstSetting.DigitsAfterDecimalPoint;
    const selectedYear = this.campaignYearStore.getSelectedCampaignYear();
    const generalConfigs: ApDynformsConfigBase<any>[] = [];
    const applicationMode = operationModeAppSetting?.ApplicationMode
      ?? this.nFertilizationPlanningEntryService.componentData.ApplicationMode;
    if (applicationMode !== ApplicationModesEnum.Constant) {
      generalConfigs.push(...[
        new ApDynformsConfigNumerictextbox({
          key: 'ECStage',
          label: 'Global__ECStage',
          value: formValues['ECStage'],
          step: 1,
          decimals: 0,
          format: 'n0',
          min: 1,
          max: 99,
          focused: dynForm.getLastFocusedControl() === 'ECStage',
          validators: [
            new ApDynformsValidator({
              validator: Validators.required,
              errorKey: 'Settings__Msg_Vali_Value_Required'
            }),
            new ApDynformsValidator({
              validator: Validators.min(growthStagesMinAndMax.Min),
              errorKey: 'AP_TK_Value_Too_Low'
            }),
            new ApDynformsValidator({
              validator: Validators.max(growthStagesMinAndMax.Max),
              errorKey: 'AP_TK_Value_Too_High'
            })
          ]
        }),
        new ApDynformsConfigComboBox({
          key: 'OperationMode',
          label: 'Global__OperationMode',
          valueField: nameOf<IOperationModeAppSetting>('Id'),
          textField: nameOf<IOperationModeAppSetting>('Text'),
          groupByField: nameOf<IOperationModeAppSetting>('Category'),
          options: operationModeAppSettings$,
          value: operationModeAppSetting?.Id,
          infoText: 'Infotext__NF_OperationMode',
          translate: true,
          validators: [
            new ApDynformsValidator({validator: Validators.required})
          ]
        })
      ]);
    }
    generalConfigs.push(...[
      new ApDynformsConfigDatepicker({
        key: 'FertilizationDate',
        label: 'Nutrients__FertilizationDate',
        minDate: this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultStart)),
        maxDate: this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultEnd).add(1, 'days')),
        validators: [
          new ApDynformsValidator({
            validator: Validators.required,
            errorKey: 'Settings__Msg_Vali_Value_Required'
          }),
          new ApDynformsValidator({
            validator: ApDateMinMaxValidator.ValidateDateRange(
              this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultStart)),
              this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultEnd).add(1, 'days'))),
            errorKey: ApDateMinMaxValidator.getYearRangeFormatted(this.languageStore.CurrentDateFormat, 
              this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultStart)),
              this.dateService.toJsDate(this.dateService.toFarmDateFromUtc(selectedYear.DefaultEnd)))
          }),
        ],
        value: formValues['FertilizationDate']
          ? formValues['FertilizationDate']
          : new Date(Math.min(new Date().valueOf(), new Date(selectedYear.DefaultEnd).valueOf()))
      }),
      new ApDynformsConfigTextbox({
        key: 'Note',
        label: 'Global__Note',
        value: formValues['Note']
      })
    ]);
    return [
      new ApDynformsConfigFieldset({
        key: 'Information',
        legend: 'Global__Information',
        classList: 'informational-fieldset',
        config: [
          new ApDynformsConfigTextbox({
            key: 'Count_Fields',
            label: 'AgriConnect__Count_Fields',
            informational: true,
            value: selectedFields.length
          }),
          new ApDynformsConfigTextbox({
            key: 'TotalArea',
            label: 'Nutrients__TotalArea_ha',
            value: this.roundNumericService.round(totalArea, digitsAfterDecimal),
            informational: true
          }),
          new ApDynformsConfigTextarea({
            key: 'Crops',
            label: 'Settings__Lbl_Menu_Croptypes',
            rows: crops.length > 1 ? 2 : 1,
            value: crops.length ? crops.reduce((a, b) => a === '' ? b : `${a}\n${b}`, '') : '-',
            informational: true
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'General',
        legend: 'Global__General_Information',
        config: generalConfigs
      })
    ];
  }

  public getProductFieldSet(formValues: { Group?: number, Product?: number },
                            formValues$: Observable<any>): ApDynformsConfigFieldset {
    const fertilizerOptions = this._getFertilizerOptions(formValues$);
    return new ApDynformsConfigFieldset({
      key: 'Product',
      legend: 'Global__Product',
      config: [
        new ApDynformsConfigComboBox({
          key: 'Group',
          label: 'Global__Lbl_Group',
          options: [
            {key: 0, text: 'Nutrients_Pages_Popups__Inorganic'},
            {key: 1, text: 'Nutrients_Pages_Popups__Organic'}
          ],
          textField: 'text',
          valueField: 'key',
          translate: true,
          valuePrimitive: true,
          value: formValues.Group ?? this._getDefaultGroup(),
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        }),
        new ApDynformsConfigComboBox({
          key: 'Product',
          label: 'Global__Product',
          valueField: 'Id',
          textField: 'Prodname',
          valuePrimitive: true,
          value: null,
          options: fertilizerOptions,
          value$: combineLatest([
            formValues$,
            this.fertilizerStore.OrganicFertilizer$,
            this.fertilizerStore.InorganicFertilizer$,
            fertilizerOptions
          ]).pipe(
            filter(([values]) => {
              return values['Group'] === formValues.Group || values['Group'] === -1;
            }),
            map(([values]) => {
              return values['Group'] === -1 ? 0 : this._selectableProduct(formValues['Product']);
            }),
            delay(1),
          ),
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        })
      ]
    });
  }

  public getDocuFieldSet(formValues: {
                           Driver?: IDriver,
                           Machine?: IMachine,
                           Instrument?: IInstrument,
                           HaPerHour?: number
                         },
                         formValues$: Observable<any>, currentFormValues: () => any, isModalField: boolean): ApDynformsConfigFieldset {
    return new ApDynformsConfigFieldset({
      key: 'Docu',
      legend: 'Settings__Lbl_Menu_Resources',
      useMaxWidth: isModalField,
      config: [
        new ApDynformsConfigComboBox({
          key: 'Machine',
          label: 'Settings__Lbl_Menu_Devices',
          textField: 'NameWithSign',
          valueField: 'Id',
          valuePrimitive: false,
          value: formValues.Machine ?? null,
          options: this.machineStore.Machines$,
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: 'Instrument',
          label: 'Docu_Ini__Tool',
          textField: 'Name',
          valueField: 'Id',
          valuePrimitive: false,
          value: formValues.Instrument ?? null,
          value$: formValues$.pipe(
            filter(values => !!values['Machine'] || !!values['Instrument']),
            map(values => {
              if (!!values['Instrument']) {
                return values['Instrument'];
              }
              if (!!currentFormValues['Instrument']) {
                return currentFormValues['Instrument'];
              }
              if (!!values['Machine'].DefaultInstrument) {
                return values['Machine'].DefaultInstrument;
              }
              return null;
            })
          ),
          options: this.instrumentStore.Instruments$,
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: 'Driver',
          label: 'Docu_Ini__Motorist',
          textField: 'FirstNameFullName',
          valueField: 'Id',
          valuePrimitive: false,
          value: formValues.Driver ?? null,
          value$: formValues$.pipe(
            filter(values => !!values['Machine'] || !!values['Driver']),
            map(values => {
              if (!!values['Driver']) {
                return values['Driver'];
              }
              if (!!currentFormValues['Driver']) {
                return currentFormValues['Driver'];
              }
              if (!!values['Machine'].DefaultDriver) {
                return values['Machine'].DefaultDriver;
              }
              return null;
            })
          ),
          options: this.driverStore.Drivers$,
          sort: 1
        }),
        new ApDynformsConfigNumerictextbox({
          key: 'HaPerHour',
          label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
          value: formValues.HaPerHour,
          value$: formValues$.pipe(
            startWith(null),
            pairwise(),
            filter(([_, current]) => !!current['Machine'] || !!current['Instrument'] || !!current['HaPerHour']),
            map(([previous, current]) => {
              const instrument = current['Instrument'] ?? current['Machine']?.DefaultInstrument;
              const previousInstrument = previous?.['Instrument'];
              if (instrument?.DefaultHaPerHour != null &&
                (!previousInstrument || instrument.Id !== previousInstrument.Id)) {
                return instrument.DefaultHaPerHour;
              }
              const haPerHour = current['HaPerHour'] ?? previous?.['HaPerHour'];
              if (Number.isFinite(haPerHour)) {
                return haPerHour;
              }
              if (Number.isFinite(currentFormValues['HaPerHour'])) {
                return currentFormValues['HaPerHour'];
              }
              return null;
            })
          ),
          decimals: 1,
          min: 0,
          step: 0.1
        })
      ]
    });
  }

  public modalDialogDocuFieldSet(dataItem: any, formValues$: Observable<any>): ApDynformsConfigFieldset {
    const durationInTimeSpan = ApTimeSpanUtil.getTimeSpanFromMilliseconds(
      dataItem.DurationInMilliseconds, true);
    return new ApDynformsConfigFieldset({
      key: 'Docu',
      legend: 'Settings__Lbl_Menu_Resources',
      useMaxWidth: true,
      config: [
        new ApDynformsConfigComboBox({
          key: 'Machine',
          label: 'Settings__Lbl_Menu_Devices',
          textField: 'NameWithSign',
          valueField: 'Id',
          valuePrimitive: false,
          value: dataItem.Machine ?? null,
          options: this.machineStore.Machines$,
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: 'Instrument',
          label: 'Docu_Ini__Tool',
          textField: 'Name',
          valueField: 'Id',
          valuePrimitive: false,
          value: dataItem.Instrument ?? null,
          options: this.instrumentStore.Instruments$,
          sort: 1
        }),
        new ApDynformsConfigComboBox({
          key: 'Driver',
          label: 'Docu_Ini__Motorist',
          textField: 'FirstNameFullName',
          valueField: 'Id',
          valuePrimitive: false,
          value: dataItem.Driver ?? null,
          options: this.driverStore.Drivers$,
          sort: 1
        }),
        new ApDynformsConfigPlaceholder(),
        new ApDynformsConfigNumerictextbox({
          key: 'DurationInHours',
          label: `${this.translationService.translate('Docu_Ini__Duration')} [${this.translationService.translate('Base__UnitH')}]`,
          value: durationInTimeSpan.TotalHours,
          decimals: 1,
          min: 0,
          step: 0.1
        }),
        new ApDynformsConfigNumerictextbox({
          key: 'HaPerHour',
          label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
          value: dataItem.HaPerHour,
          value$: formValues$.pipe(
            map(c => {
              if (!Number.isFinite(dataItem.Area) || !Number.isFinite(c.DurationInHours)) {
                return 0;
              }
              const result = dataItem.Area / c.DurationInHours;
              return Number.isFinite(result) ? result : 0;
            })
          ),
          decimals: 1,
          min: 0,
          step: 0.1,
          disabled: true
        })
      ]
    });
  }

  private _getFertilizerOptions(formValues$: Observable<any>): Observable<CombinedFertilizer[]> {
    return combineLatest([
      formValues$,
      this.fertilizerStore.OrganicFertilizer$,
      this.fertilizerStore.InorganicFertilizer$
    ]).pipe(
      map(([values, organic, inorganic]) => {
        let fert: CombinedFertilizer[] = [];
        switch (values['Group']) {
          case -1:
            return [{
              Id: 0,
              Prodname: this.translationService.translate('Nutrients_Pages__PureNutrient')
            }] as CombinedFertilizer[];
          case 0:
            fert = inorganic.filter((i) => i.ContentN > 0);
            break;
          case 1:
            fert = organic.filter((o) => o.ContentN > 0);
            break;
        }
        return fert.sort((first, second) => {
          if (first.Prodname == null) {
            return 1;
          }
          if (second.Prodname == null) {
            return -1;
          }
          return first.Prodname.localeCompare(second.Prodname);
        });
      })
    );
  }

  private _getDefaultGroup(): number {
    if (this.fertilizerInorganicStore.Listen(s => s.data).getValue().filter((i) => i.Selectable && i.ContentN > 0).length === 0 &&
      this.fertilizerOrganicStore.Listen(s => s.data).getValue().filter((o) => o.Selectable && o.ContentN > 0).length !== 0) {
      return 1;
    }
    return 0;
  }

  private _selectableProduct(productId: number): number {
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return null;
    }
    if (fertilizer.Selectable === true) {
      return productId;
    }
    return null;
  }
}
